<!--
* An image progress component
* Takes url of the image, shows the spinner while the image is loading,
* once the loading is done, presents the image.
*
* @param {string} imageUrl - Url of the image (typically the thumbnail)
* @param {string} fallbackUrl - Url to show if `imageUrl` never resolves (e.g.
*                               original full-size image when the derived
*                               thumbnail isn't available yet or failed)
* @param {number} maxTime - How long it should be attempted to get an image
* in case of fail
* @param {number} retryWait - How long to wait until the next attempt to get an image
* @param {string} loadingImageSrc - the location of the spinner used in loading
*
-->

<template>
  <img
    v-if="srcOfImage!==null"
    :src="srcOfImage"/>
  <span v-else></span>
</template>

<script>
/** loadingTimeout - variable for storing setTimeout
*   stores unique id of the timer
*/
let loadingTimeout = null
export default {
  name: 'MsImageProgress',
  props: {
    imageUrl: {
      type: String
    },
    fallbackUrl: {
      type: String,
      default: null
    },
    maxTime: {
      type: Number,
      default: 10000
    },
    retryWait: {
      type: Number,
      default: 1000
    },
    loadingImageSrc: {
      type: String
    }
  },
  data: function () {
    return {
      srcOfImage: this.loadingImageSrc
    }
  },
  created: function () {
    this.loadImage()
  },
  watch: {
    imageUrl (newUrl) {
      clearTimeout(loadingTimeout)
      this.srcOfImage = this.loadingImageSrc
      this.loadImage()
    }
  },
  methods: {
    /**
      * Attempts to get the image from the 'imageUrl', while that is being
      * done, the spinner is shown.
      * If it fails to get the image it will rerun until either success or
      * it has hit a certain amount of attempts.
      * If the image was not retrieved in the given time, fall back to
      * `fallbackUrl` if one is available, otherwise show nothing.
      */
    loadImage: function () {
      let ts = Date.now()
      let image = new Image()
      image.onerror = () => {
        if (Date.now() - ts < this.maxTime) {
          loadingTimeout = setTimeout(() => { image.src = this.imageUrl }, this.retryWait)
        } else if (this.fallbackUrl) {
          // Thumbnail/derived image never showed up — render the original
          // full-size upload so the user still sees their photo.
          this.srcOfImage = this.fallbackUrl
        } else {
          this.srcOfImage = null
        }
      }
      image.onload = () => {
        this.srcOfImage = this.imageUrl
      }
      image.src = this.imageUrl
    }
  }
}
</script>
